/**
 * GP Ads AI - Budget Adjustment Slider Component
 * 
 * PRD-3604: E004-S004 - Adjust Campaign Budgets
 * 
 * Features:
 * - Slider control for budget adjustment
 * - Quick adjustment buttons (+10%, -10%, etc.)
 * - Real-time validation against guardrails
 * - Approval requirement indicator
 * - Change percentage display
 */

import React, { useState, useEffect } from 'react';
import { useBudgetAdjustment } from '../../hooks/useBudgetAdjustment';
import { budgetApi } from '../../lib/budgetApi';

interface BudgetAdjustmentSliderProps {
  campaignId: string;
  campaignName: string;
  platform: 'meta' | 'google' | 'tiktok';
  currentBudget: number;
  currency?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  // Platform-specific IDs
  customerId?: string; // Google
  budgetId?: string; // Google
  advertiserId?: string; // TikTok
}

// Guardrail constants (match backend)
const MIN_BUDGET = 10;
const MAX_INCREASE_RATIO = 5;

export const BudgetAdjustmentSlider: React.FC<BudgetAdjustmentSliderProps> = ({
  campaignId,
  campaignName,
  platform,
  currentBudget,
  currency = 'USD',
  onSuccess,
  onCancel,
  customerId,
  budgetId,
  advertiserId
}) => {
  const {
    newBudget,
    setNewBudget,
    isLoading,
    error,
    changeAmount,
    changePercent,
    requiresApproval,
    approvalLevel,
    isValid,
    validationError,
    adjustBudget,
    reset,
    quickAdjustments,
    applyQuickAdjustment
  } = useBudgetAdjustment(currentBudget, {
    onSuccess: (result) => {
      onSuccess?.();
    }
  });

  // Calculate slider bounds
  const minSliderValue = MIN_BUDGET;
  const maxSliderValue = currentBudget * MAX_INCREASE_RATIO;

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setNewBudget(Math.round(value * 100) / 100);
    }
  };

  // Handle slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setNewBudget(Math.round(value * 100) / 100);
  };

  // Handle form submit
  const handleSubmit = async () => {
    const result = await adjustBudget({
      campaignId,
      platform,
      newBudget,
      oldBudget: currentBudget,
      reason: `Manual adjustment: ${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(0)}% change`,
      customerId,
      budgetId,
      advertiserId
    });

    if (result) {
      // Success is handled in onSuccess callback
    }
  };

  // Get approval badge color
  const getApprovalBadgeColor = () => {
    switch (approvalLevel) {
      case 'auto_approve':
        return 'bg-green-100 text-green-800';
      case 'manager_approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'director_approval':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get approval label
  const getApprovalLabel = () => {
    switch (approvalLevel) {
      case 'auto_approve':
        return 'Auto-Approve';
      case 'manager_approval':
        return 'Manager Approval Required';
      case 'director_approval':
        return 'Director Approval Required';
      default:
        return 'Unknown';
    }
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full"
      data-testid="budget-adjustment-slider"
    >
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Adjust Budget
        </h3>
        <p className="text-sm text-gray-500 truncate" title={campaignName}>
          {campaignName}
        </p>
      </div>

      {/* Current Budget Display */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Current Budget</span>
          <span className="text-lg font-semibold text-gray-900">
            {budgetApi.formatBudget(currentBudget, currency)}/day
          </span>
        </div>
      </div>

      {/* Slider Control */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          New Budget
        </label>
        <input
          type="range"
          min={minSliderValue}
          max={maxSliderValue}
          step={10}
          value={newBudget}
          onChange={handleSliderChange}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
          data-testid="budget-slider"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{budgetApi.formatBudget(minSliderValue, currency)}</span>
          <span>{budgetApi.formatBudget(maxSliderValue, currency)}</span>
        </div>
      </div>

      {/* Manual Input */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">$</span>
          <input
            type="number"
            value={newBudget}
            onChange={handleInputChange}
            min={MIN_BUDGET}
            max={maxSliderValue}
            step={0.01}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="budget-input"
          />
          <span className="text-gray-500">/day</span>
        </div>
      </div>

      {/* Quick Adjustment Buttons */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Quick Adjustments
        </label>
        <div className="flex flex-wrap gap-2">
          {quickAdjustments.map((adj) => (
            <button
              key={adj.label}
              onClick={() => applyQuickAdjustment(adj.value)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                Math.abs(newBudget - adj.value) < 0.01
                  ? 'bg-blue-100 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`quick-adjust-${adj.label}`}
            >
              {adj.label}
            </button>
          ))}
        </div>
      </div>

      {/* Change Summary */}
      <div className="mb-4 p-3 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">New Budget</span>
          <span className="text-lg font-semibold text-gray-900">
            {budgetApi.formatBudget(newBudget, currency)}/day
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Change</span>
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-medium ${
                changePercent > 0 ? 'text-green-600' : changePercent < 0 ? 'text-red-600' : 'text-gray-600'
              }`}
              data-testid="change-percent"
            >
              {budgetApi.formatChangePercent(changePercent)}
            </span>
            <span className="text-sm text-gray-500">
              ({changeAmount >= 0 ? '+' : ''}
              {budgetApi.formatBudget(changeAmount, currency)}/day)
            </span>
          </div>
        </div>
      </div>

      {/* Approval Status */}
      <div className="mb-4">
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getApprovalBadgeColor()}`}
          data-testid="approval-badge"
        >
          {getApprovalLabel()}
        </span>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div 
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
          data-testid="validation-error"
        >
          {validationError}
        </div>
      )}

      {/* API Error */}
      {error && (
        <div 
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
          data-testid="api-error"
        >
          {error.message}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          data-testid="cancel-button"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!isValid || isLoading}
          className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            isValid && !isLoading
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
          data-testid="submit-button"
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : requiresApproval ? (
            'Request Approval'
          ) : (
            'Apply Change'
          )}
        </button>
      </div>

      {/* Platform Info */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Platform: {platform.charAt(0).toUpperCase() + platform.slice(1)} • 
          Campaign ID: {campaignId.substring(0, 8)}...
        </p>
      </div>
    </div>
  );
};

export default BudgetAdjustmentSlider;
